/* inno-bounce.js — the top of a page gives a little.
   Arriving at the top of the shop lattice or a product page used to stop dead: the native
   rubber band is switched off site-wide (overscroll-behavior: none, because it dragged the
   content off its ground and showed bare page), so a hard flick just hit a wall under the header.

   Now the PAGE CONTENT answers the hit instead. The cards, or a product's photographs and text,
   drop a short way under the header, as if the momentum carried through, and spring back into
   place. What shows in the gap is a deliberate ground (the page's own first photograph, blurred
   to its tones), not an accident. The header never moves.
     · a flick that reaches the top at speed  → the content drops by how hard it arrived
     · a finger pulling down at the top       → the content follows, with resistance, and springs back on release
     · a wheel or trackpad pushing up at the top (desktop) → a smaller version of the same

   The drop is the CSS `translate` property, so it never collides with a `transform` anything
   else sets on these blocks. Browsers without `translate` just keep the hard stop. */
(function () {
  'use strict';

  if (!(window.CSS && CSS.supports && CSS.supports('translate', '0 1px'))) return;

  var root = document.documentElement;
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // reduced motion softens it rather than removing it: a frozen feature reads as broken
  var SCALE = REDUCE ? 0.5 : 1;

  var W = 0.0085;       // spring rate (rad/ms), critically damped: peaks ~120ms after the hit, home by ~0.6s
  var MAX_HIT = 72;     // deepest drop a flick can produce (px)
  var MAX_PULL = 120;   // the resistance curve's ceiling for a finger pull (px)
  var GROUND_H = 240;   // height of the ground layer behind the gap (revealed, never stretched)

  /* THE GROUND IMAGE. Leave this empty and every page uses its own first photograph (see
     groundImage below). Paste a full image URL here — e.g. a Shopify Files link to a concrete
     texture — and every shop and product page uses that instead. */
  var GROUND_SRC = '';

  /* ---- where we are ---- */
  function blocked() {
    var b = document.body.classList;
    // anything layered over the page owns the gesture
    return b.contains('cart-win-open') || b.contains('modal-open') ||
      !!document.querySelector('.page-header.nav-open, .inno-header.nav-open');
  }
  // A transformed or translated block becomes the containing block for any position:fixed
  // descendant and drags it along. The headers and the buy bar must stay pinned.
  function hasFixedChild(el) {
    var kids = el.querySelectorAll('*');
    for (var i = 0, n = Math.min(kids.length, 600); i < n; i++) {
      if (getComputedStyle(kids[i]).position === 'fixed') return true;
    }
    return false;
  }
  var pdpTargets = null;
  function context() {
    if (!document.body || blocked()) return null;
    var b = document.body.classList;
    if (b.contains('shop-page') && b.contains('page-settled')) {
      var shop = document.getElementById('shop');
      var grid = shop && shop.querySelector(':scope > .pgrid');
      if (!grid) return null;
      return { kind: 'shop', pane: shop, top: shop.scrollTop, targets: [grid] };
    }
    if (b.contains('template-product')) {
      if (!pdpTargets) {
        pdpTargets = [].slice.call(document.querySelectorAll('.inno-grid, .inno-related'))
          .filter(function (el) { return !hasFixedChild(el); });
      }
      if (!pdpTargets.length) return null;
      return { kind: 'pdp', pane: null, top: window.scrollY || root.scrollTop || 0, targets: pdpTargets };
    }
    return null;
  }

  /* ---- the ground in the gap ----
     Sits BEHIND the content and shows exactly as far as the content has dropped, so the gap reads
     as a ground the page lifts off rather than bare page. It was solid void, which on a bone page
     full of photographs read as a hole. It is now the page's own first photograph, blurred down
     to its tones: the concrete these pieces are shot on, so the gap looks like more of the same
     surface. Revealed with a clip, never stretched, so the picture keeps its proportions.
     On the shop it lives inside the scrolling lattice, just before the grid. On a product page it
     is fixed to the top of the viewport, first in <body>, so the translated content (a later
     stacking context) paints over it. */
  var grounds = {};
  function groundFor(ctx) {
    var g = grounds[ctx.kind];
    if (g && g.isConnected) return g;
    g = document.createElement('div');
    g.className = 'inno-bounce-ground';
    g.setAttribute('aria-hidden', 'true');
    var s = g.style;
    s.position = ctx.kind === 'shop' ? 'absolute' : 'fixed';
    s.left = '0'; s.right = '0'; s.top = '0'; s.height = GROUND_H + 'px';
    s.overflow = 'hidden';
    // a concrete-grey stand-in for the instant before the photograph is drawn — never white or black
    s.backgroundColor = '#8a857d';
    s.clipPath = 'inset(0 0 100% 0)';
    s.pointerEvents = 'none'; s.zIndex = '0';
    var pic = document.createElement('i');
    var ps = pic.style;
    // oversized so the blur has picture to spread into rather than fading to the edge colour
    ps.position = 'absolute'; ps.left = '-40px'; ps.right = '-40px'; ps.top = '-40px'; ps.bottom = '-40px';
    ps.backgroundSize = 'cover'; ps.backgroundPosition = 'center top'; ps.backgroundRepeat = 'no-repeat';
    ps.filter = 'blur(18px) brightness(0.92)';
    g.appendChild(pic);
    if (ctx.kind === 'shop') ctx.pane.insertBefore(g, ctx.targets[0]);
    else document.body.insertBefore(g, document.body.firstChild);
    grounds[ctx.kind] = g;
    return g;
  }
  // the photograph the ground is made from: the hard-coded image if there is one, otherwise the
  // page's own — the product page's ground image (its "Background image" setting, else the
  // piece's featured photo), or the first card in the shop. The CDN address, never the in-page
  // blob copy the loader may hold, which can be released once the card has drawn.
  function groundImage(ctx) {
    if (GROUND_SRC) return GROUND_SRC;
    var el;
    if (ctx.kind === 'pdp') {
      el = document.querySelector('[data-inno-bleed]') || document.querySelector('[data-inno-mainimg]') ||
           document.querySelector('.inno-thumb img');
    } else {
      // a loaded card swaps its photo for an in-page copy and drops the address, so the first
      // visible card is looked up in the catalogue by its link instead
      var list = window.INNO_PRODUCTS || [];
      var cells = ctx.targets[0].querySelectorAll('.pcell');
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].style.display === 'none') continue;
        var href = cells[i].getAttribute('href');
        for (var j = 0; j < list.length; j++) if (list[j].url === href && list[j].img) return list[j].img;
        el = cells[i].querySelector('.p-img');
        break;
      }
      if (!el && list[0]) return list[0].img || '';
    }
    if (!el) return '';
    var src = el.getAttribute('data-inno-src') || el.getAttribute('src') || '';
    return src.indexOf('blob:') === 0 ? '' : src;
  }
  function dressGround(ctx, g) {
    var src = groundImage(ctx);
    if (!src || g._src === src) return;
    g._src = src;
    g.firstChild.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
  }

  /* ---- the spring ---- */
  var x = 0, v = 0, mode = 'idle', active = null;   // mode: idle | spring | pull
  var raf = 0, lastT = 0, failsafe = 0;

  function paint(ctx, px) {
    if (!ctx) return;
    var on = px > 0.1;
    var t = on ? '0 ' + px.toFixed(2) + 'px' : '';
    for (var i = 0; i < ctx.targets.length; i++) ctx.targets[i].style.translate = t;
    var g = groundFor(ctx);
    if (on) dressGround(ctx, g);
    // reveal the top of the ground down to the content's edge (+1px so no hairline of page shows)
    g.style.clipPath = on ? 'inset(0 0 ' + Math.max(0, GROUND_H - px - 1).toFixed(2) + 'px 0)' : 'inset(0 0 100% 0)';
  }
  function settle() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    clearTimeout(failsafe);
    x = 0; v = 0;
    if (mode === 'spring') mode = 'idle';
    paint(active, 0);
  }
  function step(now) {
    raf = 0;
    if (mode !== 'spring') return;
    if (!context()) { settle(); return; }   // the page changed underneath (left the shop, opened the menu)
    var dt = Math.min(48, now - lastT); lastT = now;
    // fixed small substeps keep the spring stable on a slow frame
    for (var h = dt; h > 0; h -= 8) {
      var s = h < 8 ? h : 8;
      v += (-W * W * x - 2 * W * v) * s;
      x += v * s;
    }
    if (x <= 0.15 && v <= 0.002) { settle(); return; }
    paint(active, x);
    raf = requestAnimationFrame(step);
  }
  function spring(ctx) {
    active = ctx;
    mode = 'spring';
    if (!raf) { lastT = performance.now(); raf = requestAnimationFrame(step); }
    // the spring rides requestAnimationFrame, which a backgrounded tab can stall — never leave
    // the content hanging below the header
    clearTimeout(failsafe);
    failsafe = setTimeout(settle, 1600);
  }
  // a hit: launch the content downward so its deepest point is ~amp px
  function kick(ctx, amp) {
    if (mode === 'pull' || amp < 4) return;
    var v0 = amp * W * Math.E;
    v = Math.max(v, v0);
    spring(ctx);
  }

  /* ---- a flick arriving at the top ----
     Speed is the PEAK over the last moment, not the final delta: momentum has mostly decayed by
     the time it touches the top, so the last event alone would make a hard flick look gentle. */
  var lastPane = null, lastPos = 0, lastAt = 0, peak = 0, peakAt = 0;
  document.addEventListener('scroll', function (e) {
    var ctx = context();
    if (!ctx) { lastPane = null; return; }
    var t = e.target;
    var mine = ctx.pane ? t === ctx.pane : (t === document || t === root || t === document.body);
    if (!mine) return;
    var now = performance.now(), pos = ctx.top, paneKey = ctx.pane || document;
    if (lastPane !== paneKey) { lastPane = paneKey; lastPos = pos; lastAt = now; peak = 0; return; }
    var d = pos - lastPos, dt = Math.max(1, now - lastAt);
    lastPos = pos; lastAt = now;
    // a jump is a restore or a reset, not a flick
    if (Math.abs(d) > 280) { peak = 0; return; }
    if (now - peakAt > 220) peak = 0;
    var up = -d / dt;
    if (up > peak) { peak = up; peakAt = now; }
    if (d < 0 && pos <= 0.5) {
      kick(ctx, Math.min(MAX_HIT, Math.max(0, (peak - 0.6) * 18)) * SCALE);
      peak = 0;
    }
  }, { capture: true, passive: true });

  /* ---- a finger pulling down at the top ---- */
  function rubber(raw) { return MAX_PULL * (1 - 1 / (1 + raw * 0.55 / MAX_PULL)); }
  function unrubber(px) { return px <= 0 ? 0 : (MAX_PULL / (1 - Math.min(px, MAX_PULL - 1) / MAX_PULL) - MAX_PULL) / 0.55; }
  var sx = 0, sy = 0, ly = 0, axis = null, base = null, pullCtx = null;
  document.addEventListener('touchstart', function (e) {
    axis = null; base = null;
    if (e.touches.length !== 1) return;
    sx = e.touches[0].clientX; sy = ly = e.touches[0].clientY;
  }, { capture: true, passive: true });
  document.addEventListener('touchmove', function (e) {
    if (e.touches.length !== 1) return;
    var px = e.touches[0].clientX, py = e.touches[0].clientY;
    if (axis === null) {
      var ax = Math.abs(px - sx), ay = Math.abs(py - sy);
      if (ax < 6 && ay < 6) return;
      axis = ay > ax ? 'y' : 'x';            // sideways stays with the photo strip / cards
    }
    if (axis !== 'y') return;
    var dy = py - ly; ly = py;
    if (base === null) {
      if (dy <= 0) return;
      var ctx = context();
      if (!ctx || ctx.top > 0.5) return;
      // the bars aren't content: a drag that starts on them isn't a pull
      if (e.target.closest && e.target.closest('.page-header, .inno-header, .inno-buybar, #inno-cart-drawer')) return;
      pullCtx = ctx;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      clearTimeout(failsafe);
      mode = 'pull'; active = ctx;
      // measured from where the finger was BEFORE this move, so the move that starts the pull
      // already counts — and from wherever a bounce already is, so there's no jump
      base = (py - dy) - unrubber(x);
    }
    var raw = py - base;
    if (raw <= 0) {                           // back into the content: the scroll takes over
      base = null; mode = 'idle'; x = 0; v = 0; paint(pullCtx, 0);
      return;
    }
    // closing the gap comes before scrolling the page
    if (dy < 0 && e.cancelable) e.preventDefault();
    x = rubber(raw) * SCALE;
    paint(pullCtx, x);
  }, { capture: true, passive: false });
  function release() {
    if (mode !== 'pull') return;
    base = null; v = 0;
    spring(pullCtx);
  }
  document.addEventListener('touchend', release, { capture: true, passive: true });
  document.addEventListener('touchcancel', release, { capture: true, passive: true });

  /* ---- a wheel pushing up at the top (desktop) ----
     A trackpad keeps sending momentum after the eased scroll has arrived, and a mouse sends the
     next notch; either one landing while the pane is already at the top counts as the hit. One
     bounce per push, never a stutter of them. */
  var wAcc = 0, wAt = 0, wKick = 0;
  window.addEventListener('wheel', function (e) {
    if (e.ctrlKey || e.deltaY >= 0) return;
    var ctx = context();
    if (!ctx || ctx.top > 0.5) return;
    // over an inner pane that still has room above, that pane scrolls — nothing to hit
    var inner = e.target.closest && e.target.closest('.inno-detail, .inno-gallery, .inno-desc');
    if (inner && inner.scrollTop > 0) return;
    var now = performance.now();
    if (now - wAt > 160) wAcc = 0;
    wAt = now;
    wAcc += -e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
    if (mode !== 'idle' || now - wKick < 450 || wAcc < 30) return;
    wKick = now;
    kick(ctx, Math.min(48, 10 + wAcc * 0.25) * SCALE);
  }, { passive: true });

  // a product page restored from the back/forward cache may have been frozen mid-bounce
  window.addEventListener('pageshow', function () { mode = 'idle'; settle(); });
  // console hook: judge the feel without a phone (amp in px)
  window.innoBounce = function (amp) { var ctx = context(); if (!ctx) return 'not on the shop or a product page'; kick(ctx, amp || 48); return 'bounced'; };
})();
